<template>
    <article class="mx-auto max-w-3xl">
        <CHeading class="mb-10">{{ title }}</CHeading>

        <!-- Documents live in app/assets/legal/*.html (moved from blog.growni.sk, text unchanged). -->
        <div class="legal-document blog-prose" v-html="html" />
    </article>
</template>

<script setup lang="ts">
defineProps<{
    title: string
    html: string
}>()
</script>

<style scoped>
/* blog-prose supplies colours, links, tables and dark mode; a legal text needs a denser,
   calmer reading size than an article. */
.legal-document {
    /* The article (max-w-3xl) sets the width; stay aligned with the title. */
    max-width: none;
    margin-inline: 0;
    font-size: 1rem;
    line-height: 1.7;
}

.legal-document :deep(:is(p, ul, ol, table)) {
    margin-block: 0 1em;
}

/* blog-prose turns the first paragraph into a standfirst; a legal text has none. */
.legal-document :deep(> p:first-child) {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    margin-bottom: 1em;
}

.legal-document :deep(h2) {
    font-size: 1.25rem;
    margin-block: 2.25em 0.75em;
}

.legal-document :deep(h3) {
    font-size: 1.0625rem;
    margin-block: 1.75em 0.5em;
}

.legal-document :deep(li) {
    margin-block: 0.4em;
}

.legal-document :deep(ul > li::marker) {
    color: var(--blog-muted);
    font-weight: 400;
}

/* Numbered clauses as the texts cite them ("bod 5.2"): 1. → 1.1 → a) */
.legal-document :deep(ol) {
    /* !important: WordPress left inline list-style-type on some lists */
    list-style: none !important;
    padding-left: 0;
    counter-reset: clause;
}

.legal-document :deep(ol > li) {
    position: relative;
    padding-left: 2.25em;
    counter-increment: clause;
}

.legal-document :deep(ol > li::before) {
    content: counters(clause, '.') '.';
    position: absolute;
    left: 0;
    color: var(--blog-heading);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.legal-document :deep(ol ol > li) {
    padding-left: 3em;
}

.legal-document :deep(ol ol > li::before) {
    content: counters(clause, '.');
}

.legal-document :deep(ol[style*='lower-alpha'] > li) {
    padding-left: 1.75em;
}

.legal-document :deep(ol[style*='lower-alpha'] > li::before) {
    content: counter(clause, lower-alpha) ')';
    font-weight: 400;
}

/* Top-level clauses open with their title in bold (VOP B2B): show them as section headings,
   number and title on one line, flush with the text. */
.legal-document :deep(> ol > li) {
    margin-top: 2.25em;
    padding-left: 0;
}

.legal-document :deep(> ol > li::before) {
    position: static;
    margin-right: 0.4em;
}

.legal-document :deep(> ol > li > ol) {
    margin-top: 0.5em;
}

.legal-document :deep(> ol > li::before),
.legal-document :deep(> ol > li > strong:first-child) {
    font-family: var(--font-family-heading);
    font-size: 1.125rem;
    line-height: 1.5;
}

.legal-document :deep(> ol > li > strong:first-child) {
    color: var(--blog-heading);
}
</style>
